/*
 * MC Rent Bike Ragusa – calendario di prenotazione noleggio
 * Bikesupermarket (Shopify, tema Horizon)
 *
 * Legge le prenotazioni attive da /pages/disponibilita-noleggio (metaobject "prenotazione_noleggio"),
 * calcola la disponibilità per prodotto/taglia/giorno e aggiunge al carrello la variante
 * "Taglia / N giorni" con le date come proprietà della riga (_inizio, _fine, _taglia).
 * Shopify Flow trasforma poi ogni riga dell'ordine in una prenotazione.
 *
 * Durata: il cliente sceglie anche l'orario di ritiro e di riconsegna (fasce del negozio dal metaobject
 * impostazioni_noleggio). Ogni giorno di noleggio vale 24 ore dall'orario di ritiro: ritiro il 4 alle 9:00
 * e riconsegna il 5 entro le 9:00 = 1 giorno. Nello stesso giorno, fino a N ore = mezza giornata
 * (se il mezzo ha la tariffa). La disponibilità resta per giorno di calendario.
 */
(() => {
  if (customElements.get('mc-noleggio')) return;

  /* ---------- utilità date (sempre a mezzogiorno locale: niente sorprese con l'ora legale) ---------- */
  const pad = (n) => String(n).padStart(2, '0');
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const parse = (s) => {
    const [y, m, d] = String(s).split('-').map(Number);
    return new Date(y, m - 1, d, 12);
  };
  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  const diffDays = (a, b) => Math.round((parse(b) - parse(a)) / 86400000);
  const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();

  /* ---------- orari (minuti dalla mezzanotte) ---------- */
  const hhmm = (m) => `${Math.floor(m / 60)}:${pad(m % 60)}`;
  const toMin = (s) => {
    const m = String(s).trim().match(/^(\d{1,2})(?:[:.](\d{2}))?$/);
    return m ? Number(m[1]) * 60 + Number(m[2] || 0) : null;
  };
  const DAY_KEYS = { dom: 0, lun: 1, mar: 2, mer: 3, gio: 4, ven: 5, sab: 6 };
  const DEFAULT_HOURS = 'Lun-Ven 9:00-13:00 15:30-19:30; Sab 9:30-13:00 16:30-19:30';
  // righe tipo "Lun-Ven 9:00-13:00 15:30-19:30" (anche separate da ";") → { 1: [[540, 780], [930, 1170]], ... }
  function parseHours(text) {
    const out = {};
    for (const raw of String(text || '').split(/\n|;/)) {
      const line = raw.trim().toLowerCase();
      const m = line.match(/^([a-zàèéìòù,\s–-]+?)\s*:?\s+(\d.*)$/);
      if (!m) continue;
      const days = new Set();
      for (const part of m[1].split(',')) {
        const [a, b] = part.split(/[-–]/).map((x) => DAY_KEYS[x.trim().slice(0, 3)]);
        if (a == null) continue;
        if (b == null) {
          days.add(a);
          continue;
        }
        for (let d = a, i = 0; i < 7; d = (d + 1) % 7, i += 1) {
          days.add(d);
          if (d === b) break;
        }
      }
      const ranges = [];
      for (const r of m[2].matchAll(/(\d{1,2}(?:[:.]\d{2})?)\s*[-–]\s*(\d{1,2}(?:[:.]\d{2})?)/g)) {
        const a = toMin(r[1]);
        const b = toMin(r[2]);
        if (a != null && b != null && b > a) ranges.push([a, b]);
      }
      if (ranges.length) for (const d of days) out[d] = (out[d] || []).concat(ranges);
    }
    return out;
  }

  const fmtDay = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  const fmtProp = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  const fmtMonth = new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' });
  const fmtAria = new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const WEEKDAYS = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

  const decode = (s) => {
    const t = document.createElement('textarea');
    t.innerHTML = s || '';
    return t.value;
  };

  function formatMoney(cents, format) {
    const value = Number(cents || 0) / 100;
    const group = (n, dec, thousands, decimal) => {
      const [int, frac] = n.toFixed(dec).split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount':
          return group(value, 2, ',', '.');
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        default:
          return group(value, 2, '.', ',');
      }
    });
  }

  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === false || v == null) continue;
      if (k === 'class') node.className = v;
      else if (k === 'text') node.textContent = v;
      else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v === true ? '' : v);
    }
    for (const c of [].concat(children)) if (c) node.append(c);
    return node;
  };

  /* ---------- prenotazioni (condivise tra tutti i calendari della pagina) ---------- */
  let bookingsPromise = null;
  function loadBookings(endpoint, force = false) {
    if (bookingsPromise && !force) return bookingsPromise;
    bookingsPromise = (async () => {
      const all = [];
      const v = Date.now();
      let page = 1;
      let pages = 1;
      do {
        const sep = endpoint.includes('?') ? '&' : '?';
        // Niente "Accept: application/json": Shopify risponderebbe con il JSON standard della pagina
        // invece di renderizzare il template page.disponibilita-noleggio.
        const res = await fetch(`${endpoint}${sep}page=${page}&v=${v}`, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!res.ok) throw new Error(`Disponibilità non raggiungibile (HTTP ${res.status})`);
        const data = JSON.parse((await res.text()).trim());
        // se la risposta non è quella attesa meglio bloccare la prenotazione che rischiare doppioni
        if (!Array.isArray(data.prenotazioni)) throw new Error('Risposta di disponibilità non valida');
        all.push(...data.prenotazioni);
        pages = Number(data.pagine) || 1;
        page += 1;
      } while (page <= pages && page <= 40);
      return all;
    })();
    bookingsPromise.catch(() => {
      bookingsPromise = null;
    });
    return bookingsPromise;
  }

  async function loadCart() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const cart = await res.json();
      return cart.items || [];
    } catch (e) {
      return [];
    }
  }

  /* ---------- componente ---------- */
  class McNoleggio extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const cfgNode = this.querySelector('script[data-noleggio-config]');
      try {
        this.cfg = JSON.parse(cfgNode.textContent);
      } catch (e) {
        console.error('[Noleggio] configurazione non valida', e);
        return;
      }

      this.moneyFormat = decode(this.cfg.moneyFormat);
      this.main = this.prepare(this.cfg.product);
      this.accessories = (this.cfg.accessories || []).map((a) => this.prepare(a)).filter((a) => a.variants.length);

      this.today = parse(iso(new Date()));
      this.firstBookable = addDays(this.today, Number(this.cfg.leadDays) || 0);
      this.lastBookable = addDays(this.today, Number(this.cfg.horizonDays) || 180);
      this.closedDates = new Set(this.cfg.closedDates || []);
      this.closedWeekdays = new Set((this.cfg.closedWeekdays || []).map(Number));

      this.hours = parseHours(this.cfg.hours);
      if (!Object.keys(this.hours).length) this.hours = parseHours(DEFAULT_HOURS);
      this.halfMinutes = Math.round(Math.max(0.5, Number(this.cfg.halfHours) || 4) * 60);
      this.slotStep = Math.max(5, Number(this.cfg.slotMinutes) || 30);

      this.bookings = [];
      this.cartItems = [];
      this.loaded = false;
      this.loadError = false;

      const firstSize = this.main.sizes.find((s) => this.fleet(this.main, s) > 0) || this.main.sizes[0];
      this.state = { size: firstSize, qty: 1, start: null, end: null, startTime: null, endTime: null, offset: 0, acc: {} };
      for (const a of this.accessories) {
        this.state.acc[a.id] = { on: false, size: a.sizes[0], qty: 1 };
      }

      this.refs = {
        sizes: this.querySelector('[data-mcn="sizes"]'),
        sizeWrap: this.querySelector('[data-mcn="size-wrap"]'),
        qty: this.querySelector('[data-mcn="qty"]'),
        qtyMinus: this.querySelector('[data-mcn="qty-minus"]'),
        qtyPlus: this.querySelector('[data-mcn="qty-plus"]'),
        qtyHint: this.querySelector('[data-mcn="qty-hint"]'),
        calendar: this.querySelector('[data-mcn="calendar"]'),
        prev: this.querySelector('[data-mcn="prev"]'),
        next: this.querySelector('[data-mcn="next"]'),
        status: this.querySelector('[data-mcn="status"]'),
        summary: this.querySelector('[data-mcn="summary"]'),
        tariffs: this.querySelector('[data-mcn="tariffs"]'),
        accessories: this.querySelector('[data-mcn="accessories"]'),
        accWrap: this.querySelector('[data-mcn="acc-wrap"]'),
        total: this.querySelector('[data-mcn="total"]'),
        button: this.querySelector('[data-mcn="add"]'),
        error: this.querySelector('[data-mcn="error"]'),
        fromPrice: this.querySelector('[data-mcn="from-price"]'),
      };

      // orari di ritiro e riconsegna (compaiono dopo aver scelto il giorno)
      const idS = `mcn-ts-${this.cfg.blockId}`;
      const idE = `mcn-te-${this.cfg.blockId}`;
      this.refs.timeStart = el('select', {
        class: 'mcn-select',
        id: idS,
        onchange: (e) => {
          if (e.target.value === '') return;
          this.state.startTime = Number(e.target.value);
          const ends = this.endSlots();
          if (this.state.end && !ends.includes(this.state.endTime)) this.state.endTime = this.defaultEndTime();
          this.update();
        },
      });
      this.refs.timeEnd = el('select', {
        class: 'mcn-select',
        id: idE,
        onchange: (e) => {
          if (e.target.value === '') return;
          this.state.endTime = Number(e.target.value);
          this.update();
        },
      });
      const halfH = this.halfMinutes / 60;
      const halfLabel = Number.isInteger(halfH) ? String(halfH) : halfH.toFixed(1).replace('.', ',');
      this.halfLabel = `fino a ${halfLabel} ${halfH === 1 ? 'ora' : 'ore'}`;
      const hasHalf = this.main.minDays <= 1 && this.main.variants.some((v) => v.half);
      this.refs.timeWrap = el('div', { class: 'mcn__row mcn-time', hidden: true }, [
        el('p', { class: 'mcn__label', text: 'Orari' }),
        el('div', { class: 'mcn-time__grid' }, [
          el('label', { class: 'mcn-time__field', for: idS }, [el('span', { text: 'Ritiro alle' }), this.refs.timeStart]),
          el('label', { class: 'mcn-time__field', for: idE }, [
            el('span', { text: 'Riconsegna entro le' }),
            this.refs.timeEnd,
          ]),
        ]),
        el('p', {
          class: 'mcn__hint mcn-time__hint',
          text:
            "Ogni giorno di noleggio vale 24 ore dall'orario di ritiro" +
            (hasHalf ? `; nello stesso giorno, ${this.halfLabel} è mezza giornata.` : '.') +
            (this.main.minDays > 1 ? ` Noleggio minimo ${this.main.minDays} giorni.` : ''),
        }),
      ]);
      this.refs.summary?.before(this.refs.timeWrap);

      this.refs.qtyMinus?.addEventListener('click', () => this.setQty(this.state.qty - 1));
      this.refs.qtyPlus?.addEventListener('click', () => this.setQty(this.state.qty + 1));
      this.refs.prev?.addEventListener('click', () => this.move(-1));
      this.refs.next?.addEventListener('click', () => this.move(1));
      this.refs.button?.addEventListener('click', () => this.addToCart());
      this.refs.calendar?.addEventListener('click', (ev) => {
        const btn = ev.target.closest('[data-day]');
        if (btn && !btn.disabled) this.pick(btn.dataset.day);
      });

      // due mesi affiancati solo se il blocco è abbastanza largo (non dipende dalla finestra ma dalla colonna)
      this.months = this.monthsFor(this.clientWidth);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver((entries) => {
          const m = this.monthsFor(entries[0].contentRect.width);
          if (m !== this.months) {
            this.months = m;
            this.renderCalendar();
          }
        });
        this.ro.observe(this);
      }

      this.renderFromPrice();
      this.renderSizes();
      this.update();
      this.load();
    }

    disconnectedCallback() {
      this.ro?.disconnect();
    }

    monthsFor(width) {
      return width >= 600 ? 2 : 1;
    }

    async load(force = false) {
      this.setStatus('Verifico la disponibilità…');
      try {
        const [bookings, cart] = await Promise.all([loadBookings(this.cfg.endpoint, force), loadCart()]);
        this.bookings = bookings;
        this.cartItems = cart;
        this.loaded = true;
        this.loadError = false;
        this.setStatus('');
      } catch (e) {
        console.error('[Noleggio]', e);
        this.loadError = true;
        this.setStatus('Non riesco a verificare la disponibilità in questo momento. Ricarica la pagina o contattaci.');
      }
      this.update();
    }

    /* ----- dati prodotto ----- */
    prepare(p) {
      // "Mezza giornata" vale come 1 giorno di calendario, con una tariffa a parte
      const variants = (p.variants || [])
        .map((v) => {
          const half = /mezz/i.test(String(v.dur));
          return { ...v, half, days: half ? 1 : parseInt((String(v.dur).match(/\d+/) || ['0'])[0], 10) };
        })
        .filter((v) => v.days > 0);
      const sizes = [...new Set(variants.map((v) => v.size))];
      const flotta = {};
      for (const [k, v] of Object.entries(p.flotta || {})) flotta[norm(k)] = Number(v) || 0;
      return {
        ...p,
        variants,
        sizes: sizes.length ? sizes : ['Unica'],
        single: !p.hasSize,
        flotta,
        minDays: Math.max(1, Number(p.minDays) || 1),
        maxDays: variants.length ? Math.max(...variants.map((v) => v.days)) : 0,
      };
    }

    fleet(prod, size) {
      const keys = Object.keys(prod.flotta);
      if (!keys.length) return 1; // flotta non compilata: 1 pezzo per taglia
      if (prod.single) return prod.flotta.unica ?? prod.flotta[keys[0]];
      return prod.flotta[norm(size)] ?? 0;
    }

    used(prod, size, day, skipCart = false) {
      let n = 0;
      for (const b of this.bookings) {
        if (Number(b.p) !== Number(prod.id)) continue;
        if (!prod.single && norm(b.t) !== norm(size)) continue;
        if (b.s <= day && day <= b.e) n += Number(b.q) || 1;
      }
      if (!skipCart) {
        for (const it of this.cartItems) {
          if (Number(it.product_id) !== Number(prod.id)) continue;
          const pr = it.properties || {};
          if (!pr._inizio || !pr._fine) continue;
          if (!prod.single && norm(pr._taglia) !== norm(size)) continue;
          if (pr._inizio <= day && day <= pr._fine) n += Number(it.quantity) || 1;
        }
      }
      return n;
    }

    free(prod, size, day) {
      return this.fleet(prod, size) - this.used(prod, size, day);
    }

    variantFor(prod, size, days, half = false) {
      return prod.variants.find(
        (v) => v.days === days && Boolean(v.half) === Boolean(half) && (prod.single || v.size === size) && v.available
      );
    }

    halfAvailable(prod, size) {
      return prod.variants.some((v) => v.half && (prod.single || v.size === size) && v.available);
    }

    isClosed(d) {
      return (
        this.closedDates.has(iso(d)) || this.closedWeekdays.has(d.getDay()) || !(this.hours[d.getDay()] || []).length
      );
    }

    /* ----- orari e durata ----- */
    // ritiro: dall'apertura a mezz'ora prima della chiusura; riconsegna: fino all'orario di chiusura compreso
    slots(day, forReturn = false) {
      if (!day) return [];
      const ranges = this.hours[parse(day).getDay()] || [];
      const out = [];
      for (const [a, b] of ranges) {
        for (let t = a; forReturn ? t <= b : t < b; t += this.slotStep) if (!out.includes(t)) out.push(t);
      }
      if (day === iso(new Date())) {
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        return out.filter((t) => t > nowMin);
      }
      return out;
    }

    endSlots() {
      const s = this.state;
      if (!s.end) return [];
      const all = this.slots(s.end, true);
      return s.end === s.start && s.startTime != null ? all.filter((t) => t > s.startTime) : all;
    }

    // riconsegna proposta: stesso giorno → fino alla chiusura; giorni diversi → stessa ora del ritiro (o prima)
    defaultEndTime() {
      const s = this.state;
      const opts = this.endSlots();
      if (!opts.length) return null;
      if (s.end === s.start) return opts[opts.length - 1];
      const within = opts.filter((t) => t <= s.startTime);
      return within.length ? within[within.length - 1] : opts[0];
    }

    // giorni = blocchi di 24 ore dall'orario di ritiro; null se manca qualcosa
    duration() {
      const s = this.state;
      if (!s.start || !s.end) return null;
      if (s.startTime == null) return { error: 'Per quel giorno non ci sono più orari di ritiro: scegli un altro giorno.' };
      if (s.endTime == null) {
        return {
          error:
            s.end === s.start
              ? 'Per riconsegnare in giornata scegli un orario di ritiro precedente.'
              : 'Per quel giorno non ci sono orari di riconsegna: scegli un altro giorno.',
        };
      }
      const dd = diffDays(s.start, s.end);
      let mins = dd * 1440 + (s.endTime - s.startTime);
      if (mins <= 0) return { error: "L'orario di riconsegna deve essere dopo quello di ritiro." };
      // riconsegna all'apertura: se quel giorno il negozio apre dopo l'orario di ritiro (es. sabato alle 9:30),
      // chi riconsegna appena apre non paga un giorno in più
      if (dd > 0) {
        const opening = this.slots(s.end, true)[0];
        if (s.endTime === opening && opening > s.startTime) mins = Math.min(mins, dd * 1440);
      }
      const days = Math.max(1, Math.ceil(mins / 1440));
      const half =
        s.start === s.end &&
        mins <= this.halfMinutes &&
        this.main.minDays <= 1 &&
        this.halfAvailable(this.main, s.size);
      return { mins, days, half };
    }

    problem(prod, size, qty, start, end, days, half = false) {
      if (days < prod.minDays) return `La durata minima è di ${prod.minDays} giorni.`;
      if (days > prod.maxDays) {
        return `Online puoi prenotare fino a ${prod.maxDays} ${prod.maxDays === 1 ? 'giorno' : 'giorni'}: per periodi più lunghi contattaci.`;
      }
      if (!this.variantFor(prod, size, days, half)) return `Nessuna tariffa disponibile per ${days} giorni.`;
      if (this.fleet(prod, size) < qty) return 'Quantità non disponibile per questa taglia.';
      for (let d = parse(start); iso(d) <= end; d = addDays(d, 1)) {
        if (this.free(prod, size, iso(d)) < qty) return `Già prenotata per ${fmtDay.format(d)}: scegli altre date.`;
      }
      return null;
    }

    /* ----- interazioni ----- */
    setQty(q) {
      const max = Math.max(1, this.fleet(this.main, this.state.size));
      this.state.qty = Math.min(Math.max(1, q), max);
      this.update();
    }

    setSize(size) {
      this.state.size = size;
      this.state.qty = Math.min(this.state.qty, Math.max(1, this.fleet(this.main, size)));
      this.renderSizes();
      this.update();
    }

    move(step) {
      this.state.offset = Math.max(0, this.state.offset + step);
      this.renderCalendar();
    }

    pick(day) {
      const s = this.state;
      const d = parse(day);
      if (!s.start || s.end || day < s.start) {
        if (this.isClosed(d)) return this.flash('Quel giorno il negozio è chiuso: scegli un altro giorno di ritiro.');
        s.start = day;
        s.end = null;
        const opts = this.slots(day);
        s.startTime = opts.includes(s.startTime) ? s.startTime : opts.length ? opts[0] : null;
        s.endTime = null;
      } else {
        if (this.isClosed(d)) return this.flash('Quel giorno il negozio è chiuso: scegli un altro giorno di riconsegna.');
        s.end = day;
        s.endTime = this.defaultEndTime();
      }
      this.flash('');
      this.update();
    }

    flash(msg) {
      if (this.refs.error) this.refs.error.textContent = msg;
    }

    setStatus(msg) {
      if (!this.refs.status) return;
      this.refs.status.textContent = msg;
      this.refs.status.hidden = !msg;
    }

    /* ----- rendering ----- */
    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    renderFromPrice() {
      if (!this.refs.fromPrice) return;
      const full = this.main.variants.filter((v) => !v.half && v.days >= this.main.minDays);
      const halves = this.main.variants.filter((v) => v.half);
      if (!full.length && !halves.length) return;
      let text = '';
      if (full.length) {
        const minDays = Math.min(...full.map((x) => x.days));
        const min = Math.min(...full.filter((v) => v.days === minDays).map((v) => v.price));
        text = `da ${this.money(min)} ${minDays === 1 ? 'al giorno' : `per ${minDays} giorni`}`;
      }
      if (halves.length && this.main.minDays <= 1) {
        const h = this.money(Math.min(...halves.map((v) => v.price)));
        text = text ? `${text} · mezza giornata ${h}` : `da ${h} per mezza giornata`;
      }
      this.refs.fromPrice.textContent = text;
    }

    renderSizes() {
      const { sizes, single } = this.main;
      if (!this.refs.sizes) return;
      if (this.refs.sizeWrap) this.refs.sizeWrap.hidden = single;
      this.refs.sizes.replaceChildren(
        ...sizes.map((size) => {
          const n = this.fleet(this.main, size);
          return el('button', {
            type: 'button',
            class: `mcn-pill${size === this.state.size ? ' is-active' : ''}`,
            'aria-pressed': size === this.state.size ? 'true' : 'false',
            disabled: n <= 0,
            text: size,
            onclick: () => this.setSize(size),
          });
        })
      );
    }

    renderCalendar() {
      const cal = this.refs.calendar;
      if (!cal) return;
      const months = this.months || 1;
      const base = new Date(this.firstBookable.getFullYear(), this.firstBookable.getMonth() + this.state.offset, 1, 12);
      const lastMonth = new Date(this.lastBookable.getFullYear(), this.lastBookable.getMonth(), 1, 12);
      const s = this.state;

      if (this.refs.prev) this.refs.prev.disabled = s.offset === 0;
      const lastShown = new Date(base.getFullYear(), base.getMonth() + months - 1, 1, 12);
      if (this.refs.next) this.refs.next.disabled = lastShown >= lastMonth;

      const blocks = [];
      for (let m = 0; m < months; m += 1) {
        const first = new Date(base.getFullYear(), base.getMonth() + m, 1, 12);
        const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0, 12).getDate();
        const lead = (first.getDay() + 6) % 7;
        const grid = el('div', { class: 'mcn-grid', role: 'group', 'aria-label': fmtMonth.format(first) });
        for (const w of WEEKDAYS) grid.append(el('span', { class: 'mcn-wd', 'aria-hidden': 'true', text: w }));
        for (let i = 0; i < lead; i += 1) grid.append(el('span', { class: 'mcn-blank' }));

        for (let dnum = 1; dnum <= daysInMonth; dnum += 1) {
          const d = new Date(first.getFullYear(), first.getMonth(), dnum, 12);
          const day = iso(d);
          const out = d < this.firstBookable || d > this.lastBookable;
          const closed = this.isClosed(d);
          const freeN = out || !this.loaded ? 0 : this.free(this.main, s.size, day);
          const full = !out && this.loaded && freeN < s.qty;
          const isStart = s.start === day;
          const isEnd = s.end === day;
          const inRange = s.start && s.end && day > s.start && day < s.end;
          const cls = ['mcn-day'];
          if (out) cls.push('is-out');
          if (closed) cls.push('is-closed');
          if (full) cls.push('is-full');
          if (isStart) cls.push('is-start');
          if (isEnd) cls.push('is-end');
          if (inRange) cls.push('is-range');
          if (day === iso(this.today)) cls.push('is-today');
          let label = fmtAria.format(d);
          if (out) label += ', non prenotabile';
          else if (full) label += ', esaurito';
          else if (closed) label += ', negozio chiuso';
          grid.append(
            el('button', {
              type: 'button',
              class: cls.join(' '),
              'data-day': day,
              disabled: out || full || !this.loaded,
              'aria-pressed': isStart || isEnd ? 'true' : 'false',
              'aria-label': label,
              text: String(dnum),
            })
          );
        }
        blocks.push(
          el('div', { class: 'mcn-month' }, [el('p', { class: 'mcn-month__title', text: fmtMonth.format(first) }), grid])
        );
      }
      cal.replaceChildren(...blocks);
      cal.style.setProperty('--mcn-months', months);
    }

    renderTariffs() {
      if (!this.refs.tariffs) return;
      const list = this.main.variants
        .filter((v) => this.main.single || v.size === this.state.size)
        .filter((v) => (v.half ? this.main.minDays <= 1 : v.days >= this.main.minDays))
        .sort((a, b) => a.days - b.days || Number(b.half) - Number(a.half));
      this.refs.tariffs.replaceChildren(
        ...list.map((v) =>
          el('li', {}, [
            el('span', {
              text: v.half
                ? `Mezza giornata (${this.halfLabel})`
                : v.days === 1
                  ? '1 giorno (24 ore)'
                  : `${v.days} giorni`,
            }),
            el('span', { text: this.money(v.price) }),
          ])
        )
      );
    }

    renderQty() {
      const max = Math.max(0, this.fleet(this.main, this.state.size));
      if (this.refs.qty) this.refs.qty.textContent = String(this.state.qty);
      if (this.refs.qtyMinus) this.refs.qtyMinus.disabled = this.state.qty <= 1;
      if (this.refs.qtyPlus) this.refs.qtyPlus.disabled = this.state.qty >= max;
      if (this.refs.qtyHint) {
        this.refs.qtyHint.textContent = max > 0 ? `In flotta: ${max}` : 'Non disponibile in questa taglia';
      }
    }

    renderAccessories(days) {
      const wrap = this.refs.accessories;
      if (!wrap) return 0;
      if (this.refs.accWrap) this.refs.accWrap.hidden = !this.accessories.length;
      const s = this.state;
      let extra = 0;
      const rows = this.accessories.map((a) => {
        const sel = s.acc[a.id];
        const v = days ? this.variantFor(a, sel.size, days) : null;
        const prob = days && this.loaded ? this.problem(a, sel.size, sel.qty, s.start, s.end, days) : null;
        const ok = Boolean(days && v && !prob);
        if (!ok && sel.on) sel.on = false;
        if (sel.on && v) extra += v.price * sel.qty;

        const id = `mcn-acc-${a.id}-${this.cfg.blockId}`;
        const checkbox = el('input', {
          type: 'checkbox',
          id,
          checked: sel.on,
          disabled: !ok,
          onchange: (e) => {
            sel.on = e.target.checked;
            this.update();
          },
        });
        const meta = !days
          ? 'Scegli prima le date'
          : prob
            ? prob
            : `${this.money(v.price)} per ${days} ${days === 1 ? 'giorno' : 'giorni'}`;
        const controls = [];
        if (!a.single) {
          const select = el(
            'select',
            {
              class: 'mcn-select',
              'aria-label': `Taglia ${a.title}`,
              onchange: (e) => {
                sel.size = e.target.value;
                this.update();
              },
            },
            a.sizes.map((size) => el('option', { value: size, selected: size === sel.size, text: size }))
          );
          controls.push(select);
        }
        const qtySel = el(
          'select',
          {
            class: 'mcn-select',
            'aria-label': `Quantità ${a.title}`,
            onchange: (e) => {
              sel.qty = Number(e.target.value);
              this.update();
            },
          },
          Array.from({ length: Math.max(1, Math.min(10, this.fleet(a, sel.size))) }, (_, i) =>
            el('option', { value: i + 1, selected: i + 1 === sel.qty, text: `× ${i + 1}` })
          )
        );
        controls.push(qtySel);

        return el('div', { class: `mcn-acc${ok ? '' : ' is-disabled'}` }, [
          checkbox,
          a.image ? el('img', { src: a.image, alt: '', width: '48', height: '48', loading: 'lazy' }) : null,
          el('label', { for: id, class: 'mcn-acc__text' }, [
            el('span', { class: 'mcn-acc__title', text: a.title }),
            el('span', { class: 'mcn-acc__meta', text: meta }),
          ]),
          el('div', { class: 'mcn-acc__controls' }, controls),
        ]);
      });
      wrap.replaceChildren(...rows);
      return extra;
    }

    renderTimes() {
      const s = this.state;
      const r = this.refs;
      if (!r.timeWrap) return;
      r.timeWrap.hidden = !s.start;
      if (!s.start) return;
      // ricostruisce le opzioni solo se cambiano (così il menu aperto non si chiude)
      const fill = (select, opts, value, placeholder) => {
        const key = `${opts.join(',')}|${value}|${placeholder}`;
        if (select.dataset.key === key) return;
        select.dataset.key = key;
        select.replaceChildren(
          ...(opts.length
            ? opts.map((t) => el('option', { value: t, selected: t === value, text: hhmm(t) }))
            : [el('option', { value: '', text: placeholder })])
        );
        select.disabled = !opts.length;
      };
      fill(r.timeStart, this.slots(s.start), s.startTime, 'nessun orario');
      fill(r.timeEnd, s.end ? this.endSlots() : [], s.endTime, s.end ? 'nessun orario' : 'scegli il giorno');
    }

    update() {
      const s = this.state;
      this.renderQty();
      this.renderTariffs();
      this.renderCalendar();
      this.renderTimes();

      let days = 0;
      let variant = null;
      let prob = null;
      let half = false;
      const dur = this.duration();
      if (dur && dur.error) {
        prob = dur.error;
      } else if (dur) {
        days = dur.days;
        half = dur.half;
        prob = this.loaded ? this.problem(this.main, s.size, s.qty, s.start, s.end, days, half) : null;
        variant = this.variantFor(this.main, s.size, days, half);
      }

      const extra = this.renderAccessories(prob ? 0 : days);

      // riepilogo
      const when = (day, time) => `${fmtDay.format(parse(day))}${time != null ? `, ${hhmm(time)}` : ''}`;
      const sum = this.refs.summary;
      if (sum) {
        const rows = [];
        rows.push(this.summaryRow('Ritiro', s.start ? when(s.start, s.startTime) : 'scegli il giorno'));
        rows.push(this.summaryRow('Riconsegna', s.end ? when(s.end, s.endTime) : s.start ? 'scegli il giorno' : '—'));
        if (days) rows.push(this.summaryRow('Durata', half ? 'Mezza giornata' : `${days} ${days === 1 ? 'giorno' : 'giorni'}`));
        sum.replaceChildren(...rows);
      }

      if (prob) this.flash(prob);
      else if (
        this.refs.error &&
        /Già prenotata|durata|tariffa|Online puoi|Quantità|orari|riconsegna deve/.test(this.refs.error.textContent)
      )
        this.flash('');

      const ready = Boolean(this.loaded && !this.loadError && variant && !prob);
      const total = ready ? variant.price * s.qty + extra : 0;
      if (this.refs.total) this.refs.total.textContent = ready ? this.money(total) : '—';
      if (this.refs.button) {
        this.refs.button.disabled = !ready || this._busy;
        this.refs.button.setAttribute('aria-disabled', String(!ready));
      }
    }

    summaryRow(label, value) {
      return el('div', { class: 'mcn-sum__row' }, [el('dt', { text: label }), el('dd', { text: value })]);
    }

    /* ----- carrello ----- */
    async addToCart() {
      if (this._busy) return;
      const s = this.state;
      if (!s.start || !s.end) return;
      this._busy = true;
      const btn = this.refs.button;
      const label = btn ? btn.textContent : '';
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Controllo le date…';
      }

      try {
        // ricontrollo con dati freschi: qualcuno potrebbe aver prenotato nel frattempo
        await this.load(true);
        if (this.loadError) throw new Error('Non riesco a verificare la disponibilità: riprova tra poco.');
        const dur = this.duration();
        if (!dur) throw new Error('Scegli giorni e orari di ritiro e riconsegna.');
        if (dur.error) throw new Error(dur.error);
        const { days, half } = dur;
        const prob = this.problem(this.main, s.size, s.qty, s.start, s.end, days, half);
        if (prob) throw new Error(prob);
        const variant = this.variantFor(this.main, s.size, days, half);
        if (!variant) throw new Error('Tariffa non trovata per queste date.');

        const props = (prod, size, extra = {}) => ({
          Ritiro: `${fmtProp.format(parse(s.start))} ore ${hhmm(s.startTime)}`,
          Riconsegna: `${fmtProp.format(parse(s.end))} entro le ${hhmm(s.endTime)}`,
          Giorni: half ? 'Mezza giornata' : String(days),
          ...extra,
          _inizio: s.start,
          _fine: s.end,
          _ora_ritiro: hhmm(s.startTime),
          _ora_riconsegna: hhmm(s.endTime),
          _taglia: prod.single ? '' : size,
          _noleggio: 'MC Rent Bike Ragusa',
        });

        const items = [{ id: variant.id, quantity: s.qty, properties: props(this.main, s.size) }];
        for (const a of this.accessories) {
          const sel = s.acc[a.id];
          if (!sel.on) continue;
          const accProb = this.problem(a, sel.size, sel.qty, s.start, s.end, days);
          if (accProb) throw new Error(`${a.title}: ${accProb}`);
          const v = this.variantFor(a, sel.size, days);
          if (!v) throw new Error(`${a.title}: tariffa non trovata.`);
          items.push({ id: v.id, quantity: sel.qty, properties: props(a, sel.size, { 'Abbinato a': this.main.title }) });
        }

        if (btn) btn.textContent = 'Aggiungo al carrello…';
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Non è stato possibile aggiungere al carrello.');
        }
        window.location.href = `${root}cart`;
      } catch (e) {
        this.flash(e.message || String(e));
        this._busy = false;
        if (btn) btn.textContent = label;
        this.update();
      }
    }
  }

  customElements.define('mc-noleggio', McNoleggio);
})();
